import React from "react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import Config from "../../util/config";
import DayComponent from "./Day";
import {
  Day,
  getDays,
  getStartDate,
  getEndDate,
  loopDates,
} from "../../util/date";
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);

export interface Props {
  start: string;
  notBooked: string[];
  selectable: string[];
  selected: string;
  onDaySelect: (day: dayjs.Dayjs) => void;
  nights: number;
}

const Calendar = (props: Props) => {
  const month = dayjs(props.start + "-01", "YYYY-MM-DD");
  const start = getStartDate(month, Config.get("startingDay", "Mon"));
  const end = getEndDate(month, Config.get("startingDay", "Mon"));
  const selected = dayjs(props.selected, "YYYY-MM-DD");
  const endSelected =
    selected && props.nights
      ? selected.clone().add(props.nights, "days")
      : false;

  const datesAreEqual = (day: dayjs.Dayjs, secondDay: dayjs.Dayjs) => {
    return day.format("YYYY-MM-DD") == secondDay.format("YYYY-MM-DD");
  };

  const earliestDate = props.selectable.reduce(
    (earliest, current) =>
      dayjs(current).isBefore(earliest) ? dayjs(current) : earliest,
    dayjs(props.selectable[0]),
  );
  const minNights = earliestDate.diff(selected, "days");

  const days: JSX.Element[] = [];
  let prevBooked = true;
  loopDates(start, end, "day", (day: dayjs.Dayjs) => {
    const classes: string[] = ["calendar__day"];
    const attributes: Record<string, string> = {};

    let selectable = props.selectable.indexOf(day.format("YYYY-MM-DD")) !== -1;
    let dayBooked = true;
    const nextBooked =
      props.notBooked.indexOf(day.add(1, "day").format("YYYY-MM-DD")) == -1;

    if (
      Config.get("pastDaysUnavailable", "no") == "yes" &&
      day.isBefore(dayjs().startOf("day"))
    ) {
      classes.push("calendar__day--unbookable");
      selectable = false;
    } else {
      if (
        props.notBooked.indexOf(day.format("YYYY-MM-DD")) !== -1 &&
        day.isSameOrAfter(dayjs().startOf("day"))
      ) {
        classes.push("calendar__day--not-booked");
        dayBooked = false;
      }
      if (selectable) {
        classes.push("calendar__day--selectable");
      } else if (selected.isBefore(day)) {
        classes.push("calendar__day--no-checkout");
      }

      if (
        props.selected === day.format("YYYY-MM-DD") ||
        (endSelected &&
          selected.isValid() &&
          endSelected.isValid() &&
          day.isBetween(selected, endSelected, null, "[]"))
      ) {
        classes.push("calendar__day--selected");
        if (minNights && minNights > 0) {
          attributes["data-min-nights"] = String(minNights);
        }
      }

      if (
        endSelected &&
        selected.isValid() &&
        endSelected.isValid() &&
        day.isBetween(selected, endSelected, null, "[]") &&
        !datesAreEqual(day, selected) &&
        !datesAreEqual(day, endSelected)
      )
        classes.push("calendar__day--rangeSelect");

      if (datesAreEqual(day, selected))
        classes.push("calendar__day--selectedStarts");

      if (
        (endSelected && datesAreEqual(day, endSelected)) ||
        (datesAreEqual(day, selected) && selected.isValid())
      )
        classes.push("calendar__day--selectedEnds");
    }

    if (prevBooked != dayBooked && prevBooked) {
      classes.push("calendar__day--unavailable-end");
    }
    if (nextBooked != dayBooked && nextBooked) {
      classes.push("calendar__day--unavailable-start");
    }

    days.push(
      <DayComponent
        key={day.format("YYYY-MM-DD")}
        className={classes.join(" ")}
        clickable={selectable}
        classNameDay="calendar__day-number"
        onClick={props.onDaySelect}
        attributes={attributes}
        day={day}
      />,
    );
    prevBooked = dayBooked;
  });

  const daysWithRows: JSX.Element[] = [];
  let index: number = 0;
  while (days.length) {
    const row = days.splice(0, 7);
    daysWithRows.push(
      <div key={index} className="calendar__row">
        {row}
      </div>,
    );
    index += 1;
  }

  const headers = getDays(Config.get("startingDay", "Mon")).map(
    (day: Day): JSX.Element => {
      return (
        <span key={day.short} className="calendar__header">
          {day.short}
        </span>
      );
    },
  );
  return (
    <div className="calendar">
      <div className="calendar__row calendar__row--headers">{headers}</div>
      {daysWithRows}
    </div>
  );
};

export default Calendar;
